import React, { useRef } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { motion, useInView } from 'framer-motion';

const PASOS = [
  {
    num: '01',
    icon: 'fa-solid fa-comments',
    titulo: 'Consulta gratuita',
    descripcion: 'Nos cuentas tu proyecto: qué necesitas, a quién va dirigido y cuál es tu presupuesto. Sin compromiso, en 30 minutos ya tienes una propuesta.',
    color: '#2e8bc0',
  },
  {
    num: '02',
    icon: 'fa-solid fa-pen-ruler',
    titulo: 'Diseño y maqueta',
    descripcion: 'Diseñamos la imagen visual de tu web adaptada a tu identidad corporativa. La revisas y apruebas antes de que empecemos a desarrollarla.',
    color: '#f0ad4e',
  },
  {
    num: '03',
    icon: 'fa-solid fa-code',
    titulo: 'Desarrollo',
    descripcion: 'Desarrollamos la web y te mantenemos informado en cada fase del proceso. Tienes acceso a una previsualización en todo momento.',
    color: '#5cb85c',
  },
  {
    num: '04',
    icon: 'fa-solid fa-rocket',
    titulo: 'Entrega y soporte',
    descripcion: 'Publicamos tu web, configuramos el dominio y el correo corporativo. Después seguimos a tu lado con mantenimiento y soporte continuo.',
    color: '#e8702a',
  },
];

const Proceso = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  const irAContacto = () => {
    const el = document.getElementById('contacto');
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 70, behavior: 'smooth' });
  };

  return (
    <section id="proceso">
      <Container>
        <div className="text-center mb-5" ref={ref}>
          <motion.span className="badge-primary mb-3 d-inline-block" initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}}>
            Proceso de trabajo
          </motion.span>
          <motion.h2 className="section-title" initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.1 }}>
            ¿Cómo trabajamos?
          </motion.h2>
          <div className="accent-line center" />
          <motion.p className="section-subtitle" initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: 0.2 }}>
            Un proceso claro y sin sorpresas. Siempre sabrás en qué punto está tu proyecto.
          </motion.p>
        </div>

        <Row className="g-4 g-lg-3">
          {PASOS.map((paso, i) => (
            <Col key={paso.num} lg={3} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 36 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.55, delay: 0.15 + i * 0.12 }}
                style={{ height: '100%' }}
              >
                <div style={{
                  height: '100%',
                  background: 'var(--bg-card)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  borderRadius: '16px',
                  padding: '2rem 1.6rem',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'border-color 0.3s, transform 0.3s',
                  textAlign: 'center',
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = paso.color + '50'; e.currentTarget.style.transform = 'translateY(-4px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  {/* Número de fondo decorativo */}
                  <div style={{
                    position: 'absolute', top: '-10px', right: '12px',
                    fontSize: '5.5rem', fontWeight: 700, fontFamily: 'var(--font-heading)',
                    color: paso.color, opacity: 0.06, lineHeight: 1, userSelect: 'none',
                    pointerEvents: 'none',
                  }}>
                    {paso.num}
                  </div>

                  {/* Icono centrado */}
                  <div style={{
                    width: '60px', height: '60px', borderRadius: '16px',
                    background: `${paso.color}16`,
                    border: `1px solid ${paso.color}28`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 1.4rem',
                  }}>
                    <i className={paso.icon} style={{ color: paso.color, fontSize: '1.4rem' }} />
                  </div>

                  <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'white', marginBottom: '0.65rem', fontFamily: 'var(--font-heading)' }}>
                    {paso.titulo}
                  </h3>
                  <p style={{ color: '#8fa3bf', fontSize: '0.88rem', lineHeight: 1.7, margin: 0 }}>
                    {paso.descripcion}
                  </p>

                  {/* Flecha entre pasos (excepto el último) */}
                  {i < PASOS.length - 1 && (
                    <div className="d-none d-lg-flex" style={{
                      position: 'absolute', top: '50%', right: '-18px',
                      transform: 'translateY(-50%)',
                      zIndex: 10,
                      width: '34px', height: '34px',
                      borderRadius: '50%',
                      background: 'var(--bg-base)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      alignItems: 'center', justifyContent: 'center',
                    }}>
                      <i className="fa-solid fa-chevron-right" style={{ color: '#445568', fontSize: '0.7rem' }} />
                    </div>
                  )}
                </div>
              </motion.div>
            </Col>
          ))}
        </Row>

        <motion.div
          className="text-center mt-5"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.75 }}
        >
          <div style={{
            display: 'inline-block',
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            padding: '24px 36px',
          }}>
            <p style={{ color: '#8fa3bf', margin: '0 0 14px', fontSize: '0.93rem' }}>
              ¿Listo para empezar? La consulta inicial es gratuita y sin ningún compromiso.
            </p>
            <button className="btn-accent-custom" onClick={irAContacto}>
              Solicitar consulta gratuita
              <i className="fa-solid fa-arrow-right ms-2" style={{ fontSize: '0.8rem' }} />
            </button>
          </div>
        </motion.div>
      </Container>
    </section>
  );
};

export default Proceso;
